import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Image,
  Text,
  VStack,
  Heading,
  IconButton,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  HStack,
  Button,
  Divider,
  Spacer,
  Flex,
  useToast,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import {
  CloseIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AddIcon,
  DeleteIcon,
  ChatIcon,
} from "@chakra-ui/icons";
import HotelsCard from "../chat/HotelsCard"; // Ensure the correct import
import Activities from "../chat/Activities";
import RVRentalCard from "../chat/RVRentalCard";
import FlightCard from "../chat/FlightCard";
import Translate from "../../components/LanguageTranslation/Translate";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";
import ItineraryTabs from "./ItineraryTabs";
import countries from "i18n-iso-countries";
import { FaChild, FaPlane } from "react-icons/fa";
import { FaPerson } from "react-icons/fa6";
import CityTabs from "./CityTabs";
import InfoCard from "../Itinerary/InfoCard";
import FilterMenu from "./FilterMenu";
import {
  EXPLORE_SUBS,
  ACCOMMODATION_SUBS,
  TRANSPORT_SUBS,
} from "./itineraryConstants";
import axiosInstance from "../../helpers/axiosInstance";
import AuthModal from "../AuthModal";
import { SurveyPopup } from "../Popups";

const ItineraryDetails = ({
  generalInfo,
  tripId,
  lastUpdated,
  onCreateTripPlan,
  isCreating,
  onClose,
  isMobile,
}) => {
  // console.log("generalInfo", generalInfo);
  const scrollContainerRef = useRef(null);
  const toast = useToast();
  const { convertCurrency, currency } = useCurrency();
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessingTrip, setIsProcessingTrip] = useState(false);
  const [isSignUpOpen, setSignUpOpen] = useState(false);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [selectedCityIndex, setSelectedCityIndex] = useState(0);
  const [tripDetails, setTripDetails] = useState(null);
  const [accommodationFilter, setAccommodationFilter] = useState(["all"]);
  const [activityFilter, setActivityFilter] = useState(["all"]);
  const [transportationFilter, setTransportationFilter] = useState(["all"]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [filteredAccommodations, setFilteredAccommodations] = useState([]);
  const [filteredFlights, setFilteredFlights] = useState({});
  const [filteredRVs, setFilteredRVs] = useState([]);
  const [isFeedbackOpen, setFeedbackOpen] = useState(false);

  const selectedDestination =
    tripDetails?.destinations?.[selectedCityIndex] ||
    generalInfo?.destinations?.[selectedCityIndex] ||
    {};
  const cityTripPlan = selectedDestination?.trip_plan || {};
  const cityItinerary = cityTripPlan.itinerary || [];

  const countryName = selectedDestination?.country || "";

  // Filter accommodations based on selected filter
  useEffect(() => {
    if (!Array.isArray(selectedDestination?.home_to_go_api_response)) {
      setFilteredAccommodations([]);
      return;
    }
    if (accommodationFilter.includes("all")) {
      setFilteredAccommodations(selectedDestination?.home_to_go_api_response);
      return;
    }
    if (accommodationFilter.includes("none")) {
      setFilteredAccommodations([]);
      return;
    }
    setFilteredAccommodations(
      selectedDestination?.home_to_go_api_response.filter(
        (accommodation) =>
          accommodation.type && accommodationFilter.includes(accommodation.type)
      )
    );
  }, [selectedDestination?.home_to_go_api_response, accommodationFilter]);

  // Filter activities based on selected filter
  useEffect(() => {
    if (!Array.isArray(selectedDestination?.viator_api_response)) {
      setFilteredActivities([]);
      return;
    }
    if (activityFilter.includes("all")) {
      setFilteredActivities(selectedDestination?.viator_api_response);
      return;
    }
    if (activityFilter.includes("none")) {
      setFilteredActivities([]);
      return;
    }
    setFilteredActivities(
      selectedDestination?.viator_api_response.filter(
        (activity) => activity.type && activityFilter.includes(activity.type)
      )
    );
  }, [selectedDestination?.viator_api_response, activityFilter]);

  // Filter transportation based on selected filter
  const showFlights = React.useMemo(() => {
    return (
      transportationFilter.includes("all") ||
      transportationFilter.includes("flight")
    );
  }, [transportationFilter]);

  const showRVs = React.useMemo(() => {
    return (
      transportationFilter.includes("all") ||
      transportationFilter.includes("rv")
    );
  }, [transportationFilter]);

  // Determine if Create Trip Plan should be disabled
  const hasActivities =
    Array.isArray(selectedDestination?.viator_api_response) &&
    selectedDestination.viator_api_response.length > 0;
  const hasAccommodations =
    Array.isArray(selectedDestination?.home_to_go_api_response) &&
    selectedDestination.home_to_go_api_response.length > 0;
  const hasRVs =
    Array.isArray(selectedDestination?.outdoorsy_api_response) &&
    selectedDestination.outdoorsy_api_response.length > 0;
  const hasFlights =
    Array.isArray(generalInfo?.flight_info) &&
    generalInfo.flight_info.length > 0;
  // Button is disabled if no activities, no accommodations, and neither flights nor RVs
  const disableCreateTripPlan = !hasActivities;
  // || !hasAccommodations || (!hasFlights && !hasRVs)
  useEffect(() => {
    setSelectedDayIndex(0);
  }, [selectedCityIndex]);

  // Reset accommodation filter when city changes
  useEffect(() => {
    setAccommodationFilter(["all"]);
    setActivityFilter(["all"]);
    setTransportationFilter(["all"]);
  }, [selectedCityIndex]);

  // Reset tripDetails when generalInfo changes (new data from server)
  useEffect(() => {
    setTripDetails(null);
  }, [generalInfo]);

  // Filter flights for each route
  useEffect(() => {
    if (!Array.isArray(generalInfo?.flight_info)) return;
    const newFiltered = {};
    generalInfo?.flight_info.forEach((item) => {
      newFiltered[item.route] = item.flights;
    });
    setFilteredFlights(newFiltered);
  }, [generalInfo?.flight_info]);

  // Filter RVs based on selected filter
  useEffect(() => {
    if (!Array.isArray(selectedDestination?.outdoorsy_api_response)) {
      setFilteredRVs([]);
      return;
    }
    setFilteredRVs(selectedDestination?.outdoorsy_api_response);
  }, [selectedDestination?.outdoorsy_api_response]);

  // Handler to update flights after delete
  const handleFlightsChange = (route, flightIndex, type) => {
    setFilteredFlights((prev) => {
      const updated = { ...prev };
      if (type === "single" && typeof flightIndex === "number") {
        updated[route] = (updated[route] || []).filter(
          (_, idx) => idx !== flightIndex
        );
      } else if (type === "all") {
        updated[route] = [];
      }
      return updated;
    });
  };

  // AlertDialog states and refs
  const [isAlertOpen, setIsAlertOpen] = useState({
    activities: false,
    accommodations: false,
    flights: null, // route string or null
    rvs: false,
  });
  const cancelRef = useRef();
  const [pendingDelete, setPendingDelete] = useState({
    type: null, // 'activities', 'accommodations', 'flights', 'rvs'
    route: null, // for flights
  });

  // Handler to open AlertDialog for delete all
  const openDeleteAllDialog = (type, route = null) => {
    setPendingDelete({ type, route });
    setIsAlertOpen((prev) => ({
      ...prev,
      [type]: route || true,
    }));
  };
  // Handler to close AlertDialog
  const closeDeleteAllDialog = () => {
    setIsAlertOpen({
      activities: false,
      accommodations: false,
      flights: null,
      rvs: false,
    });
    setPendingDelete({ type: null, route: null });
  };

  // Handler to delete all flights for a route (uses AlertDialog)
  const [isDeletingAllFlights, setIsDeletingAllFlights] = useState({});
  const handleDeleteAllFlights = async (route) => {
    if (!tripId || !route) return;
    setIsDeletingAllFlights((prev) => ({ ...prev, [route]: true }));
    try {
      await axiosInstance.delete(
        `/chat/conversation/delete-flight-info/${tripId}`,
        {
          data: {
            route: route,
          },
        }
      );
      handleFlightsChange(route, null, "all");
    } catch (error) {
      console.error("Error deleting all flights:", error);
    } finally {
      setIsDeletingAllFlights((prev) => ({ ...prev, [route]: false }));
      closeDeleteAllDialog();
    }
  };

  // Handler to update RVs after delete
  const handleRVsChange = (rvIndex, type) => {
    if (type === "single" && typeof rvIndex === "number") {
      setFilteredRVs((prev) => prev.filter((_, idx) => idx !== rvIndex));
    } else if (type === "all") {
      setFilteredRVs([]);
    }
  };

  // Handler to delete all RVs for the city (uses AlertDialog)
  const [isDeletingAllRVs, setIsDeletingAllRVs] = useState(false);
  const handleDeleteAllRVs = async () => {
    if (!tripId || !selectedDestination?.city) return;
    setIsDeletingAllRVs(true);
    try {
      await axiosInstance.delete(
        `/chat/conversation/delete-rv-info/${tripId}`,
        {
          data: {
            city_name: selectedDestination?.city,
          },
        }
      );
      handleRVsChange(null, "all");
    } catch (error) {
      console.error("Error deleting all RVs:", error);
    } finally {
      setIsDeletingAllRVs(false);
      closeDeleteAllDialog();
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -300, // Adjust this value to control the scroll distance
        behavior: "smooth", // Smooth scrolling
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 300, // Adjust this value to control the scroll distance
        behavior: "smooth", // Smooth scrolling
      });
    }
  };

  const countryFlagLink = (countryName) => {
    const countryCode = countryName
      ? countries.getAlpha2Code(countryName, "en")
      : null;
    if (!countryCode) {
      return "../assets/images/banner.jpg";
    }
    return `https://flagcdn.com/w320/${countryCode.toLowerCase()}.png`;
  };

  const firstDayImage =
    cityItinerary?.[0]?.activity_detail_list?.[0]?.images?.[0]?.variants?.[2]
      ?.url;
  const firstImageSrc =
    firstDayImage || countryFlagLink(selectedDestination?.country);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix =
      day === 1 || day === 21 || day === 31
        ? "st"
        : day === 2 || day === 22
        ? "nd"
        : day === 3 || day === 23
        ? "rd"
        : "th";

    return date
      .toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
      .replace(day.toString(), `${day}${suffix}`);
  };

  // Function to format route with plane icon
  const formatRoute = (route) => {
    if (!route) return "";
    if (route.includes("--> ")) {
      const parts = route.split("--> ");
      return (
        <HStack spacing={2} display="inline-flex">
          <Text mb={0}>{parts[0]}</Text>
          <FaPlane />
          <Text mb={0}>{parts[1]}</Text>
        </HStack>
      );
    }
    return route;
  };

  // Handler to update activities after delete
  const handleActivitiesChange = (activityIndex, type) => {
    if (type === "single" && typeof activityIndex === "number") {
      // Update filtered activities
      setFilteredActivities((prev) =>
        prev.filter((_, idx) => idx !== activityIndex)
      );

      // Update the generalInfo data structure to reflect the deletion
      if (
        generalInfo?.destinations?.[selectedCityIndex]?.trip_plan?.itinerary?.[
          selectedDayIndex
        ]
      ) {
        const updatedGeneralInfo = { ...generalInfo };
        const day =
          updatedGeneralInfo.destinations[selectedCityIndex].trip_plan
            .itinerary[selectedDayIndex];

        // Remove the activity from activity_detail_list
        if (
          day.activity_detail_list &&
          Array.isArray(day.activity_detail_list)
        ) {
          day.activity_detail_list.splice(activityIndex, 1);
        }

        // Remove the activity from activity_name_list if it exists
        if (day.activity_name_list && Array.isArray(day.activity_name_list)) {
          day.activity_name_list.splice(activityIndex, 1);
        }

        // Force a re-render by updating the generalInfo reference
        // Note: This is a workaround since we can't directly modify the prop
        // The parent component should handle this update, but for immediate UI feedback
        // we can trigger a re-render by updating local state
        setTripDetails(updatedGeneralInfo);
      }
    } else if (type === "all") {
      setFilteredActivities([]);
    }
  };

  // Handler to update accommodations after delete
  const handleAccommodationsChange = (accommodationIndex, type) => {
    if (type === "single" && typeof accommodationIndex === "number") {
      setFilteredAccommodations((prev) =>
        prev.filter((_, idx) => idx !== accommodationIndex)
      );
    } else if (type === "all") {
      setFilteredAccommodations([]);
    }
  };

  // Handler to delete all activities for the city (uses AlertDialog)
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const handleDeleteAllActivities = async () => {
    if (!tripId || !selectedDestination?.city) return;
    setIsDeletingAll(true);
    try {
      await axiosInstance.delete(
        `/chat/conversation/delete-activities-info/${tripId}`,
        {
          data: {
            city_name: selectedDestination?.city,
          },
        }
      );
      handleActivitiesChange(null, "all");
    } catch (error) {
      console.error("Error deleting all activities:", error);
    } finally {
      setIsDeletingAll(false);
      closeDeleteAllDialog();
    }
  };

  // Handler to delete all accommodations for the city (uses AlertDialog)
  const [isDeletingAllAccommodations, setIsDeletingAllAccommodations] =
    useState(false);
  const handleDeleteAllAccommodations = async () => {
    if (!tripId || !selectedDestination?.city) return;
    setIsDeletingAllAccommodations(true);
    try {
      await axiosInstance.delete(
        `/chat/conversation/delete-accommodation-info/${tripId}`,
        {
          data: {
            city_name: selectedDestination?.city,
          },
        }
      );
      handleAccommodationsChange(null, "all");
    } catch (error) {
      console.error("Error deleting all accommodations:", error);
    } finally {
      setIsDeletingAllAccommodations(false);
      closeDeleteAllDialog();
    }
  };

  const handleSaveTrip = async () => {
    if (!tripId) {
      toast({
        title: "Error",
        description: "No conversation to save.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true); // Set loading state to true

    try {
      const response = await axiosInstance.post("/bucket-list/save", {
        conversation_id: tripId,
      });
      // console.log("response", response);
      if (response.data.success) {
        toast({
          title: "Success",
          description: response.data.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setSignUpOpen(true); // Open AuthModal on 401 error
      } else if (error.response && error.response.status === 429) {
        toast({
          title: "Error",
          description:
            "Trip saving limit reached. Wait 1 hour before saving another trip",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description:
            error.response.data.message || "Error while saving the trip",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsSaving(false); // Reset loading state
    }
  };

  // New handler for create/save trip logic
  const handleCreateOrSaveTrip = async () => {
    const isCreating = !selectedDestination?.trip_plan;

    if (isCreating) {
      // Defer spinner/toasts to parent (Chat) for creation path
      try {
        if (onCreateTripPlan) {
          if (onCreateTripPlan.constructor.name === "AsyncFunction") {
            await onCreateTripPlan();
          } else {
            onCreateTripPlan();
          }
        }
      } catch (error) {
        toast({
          title: "Error",
          description: error?.message || "Failed to create trip plan.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
      return;
    }

    // Saving existing trip plan to bucket list
    setIsProcessingTrip(true);
    toast({
      title: "Saving Trip",
      description: "Please wait while we save your trip...",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
    try {
      await handleSaveTrip();
    } catch (error) {
      toast({
        title: "Error",
        description: error?.message || "Failed to save trip.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsProcessingTrip(false);
    }
  };

  return (
    <Box
      right="0"
      top="0"
      h="100vh"
      w="100%"
      p="5"
      pt={2}
      bg="white"
      zIndex="modal"
      overflowY="auto"
      borderLeft="2px solid"
      borderColor="gray.200"
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <IconButton
          display={{ base: "block", md: "none" }}
          icon={<CloseIcon boxSize={2.5} />}
          colorScheme="gray"
          size="sm"
          isRound={true}
          position="relative"
          zIndex="tooltip"
          bg="white"
          boxShadow="md"
          aria-label="Close"
          onClick={onClose}
        />
        <Spacer />
        <HStack spacing={2}>
          <Tooltip
            label={
              disableCreateTripPlan
                ? [
                    "You can also save items like transportation, accommodation , and activites in the bucket list separately by clicking on the ♡ button.",
                    "",
                    "You need to create activities in order to create a trip plan.",
                  ]
                : ""
            }
            isDisabled={!disableCreateTripPlan}
            placement="left"
            hasArrow
            bg="gray.800"
            color="white"
            fontSize="sm"
            maxW="300px"
          >
            <Button
              size="sm"
              colorScheme="customGreen"
              onClick={handleCreateOrSaveTrip}
              isDisabled={disableCreateTripPlan || isProcessingTrip || isCreating}
              isLoading={isProcessingTrip || isCreating}
              loadingText={
                !selectedDestination?.trip_plan ? "Creating..." : "Saving..."
              }
              sx={{
                animation:
                  !disableCreateTripPlan && !isProcessingTrip
                    ? "dance 0.6s ease-in-out 6"
                    : "none",
                "@keyframes dance": {
                  "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
                  "25%": { transform: "translateY(-4px) rotate(-2deg)" },
                  "50%": { transform: "translateY(-6px) rotate(0deg)" },
                  "75%": { transform: "translateY(-4px) rotate(2deg)" },
                },
              }}
            >
              {!selectedDestination?.trip_plan
                ? "Create Trip Plan"
                : "Save Trip"}
            </Button>
          </Tooltip>
          {selectedDestination?.trip_plan && (
            <Button
              size="sm"
              bg="#FFC719"
              color="black"
              _hover={{
                transform: "scale(1.1)",
                // border: "2px solid #FFC719",
                borderColor: "#FFC719",
                bg: "#FFC719",
              }}
              leftIcon={<ChatIcon />}
              onClick={() => setFeedbackOpen(true)}
            >
              Feedback
            </Button>
          )}
        </HStack>
      </Box>
      <VStack spacing={4} align="stretch" pt="2">
        {/* Main Image and Trip Name Box - only show if generalInfo is present */}
        {generalInfo && (
          <Box position="relative" w="100%" borderRadius="xl" overflow="hidden">
            <Image
              src={firstImageSrc}
              alt={selectedDestination?.city || "No Description Available"}
              objectFit="cover"
              w="100%"
              h="300px"
            />
            <Box
              position="absolute"
              bottom="0"
              left="0"
              w="100%"
              color="white"
              p={4}
              textAlign="left"
            >
              <Text
                mb={0}
                fontSize="2xl"
                fontWeight="bold"
                width="90%"
                noOfLines={2}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: "2",
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <Translate text={generalInfo?.itinerary?.trip_name} />
              </Text>

              <Text fontSize="sm" mb={0}>
                <Translate
                  text={`Last updated: ${
                    lastUpdated ? formatDate(lastUpdated.split(" ")[0]) : "N/A"
                  }`}
                />
              </Text>
            </Box>
          </Box>
        )}
        {Array.isArray(generalInfo?.destinations) &&
          generalInfo?.destinations.length > 0 && (
            <VStack align="start" spacing={1}>
              {generalInfo?.destinations.map((dest, idx) => {
                // Calculate departure for this city
                const nextDest = generalInfo?.destinations[idx + 1];
                const departure =
                  nextDest?.arrival_date || generalInfo.return_date || "N/A";
                return (
                  <HStack key={idx} spacing={2} align="center">
                    <Text fontWeight="bold" fontSize="sm" mb={0}>
                      <Translate text={dest?.city} />,{" "}
                      <Translate text={dest?.country} />:
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={0}>
                      <Translate
                        text={`${formatDate(dest?.arrival_date)} - ${formatDate(
                          departure
                        )}`}
                      />
                      {dest?.days_to_stay && (
                        <span> ({dest?.days_to_stay} days)</span>
                      )}
                    </Text>
                  </HStack>
                );
              })}
            </VStack>
          )}

        <Divider m={0} p={0} />
        {generalInfo?.itinerary?.trip_description && (
          <Text size={{ base: "sm", md: "md" }} mb={0}>
            <Translate text={generalInfo?.itinerary?.trip_description} />
          </Text>
        )}
        <Accordion
          allowMultiple
          border="2px solid"
          borderColor="red.500"
          mt={4}
          mb={0}
        >
          <AccordionItem>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                <Heading fontSize="18px" fontFamily="Arial" mb={0}>
                  <Translate text="Itinerary" />
                </Heading>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <InfoCard
                tripId={tripId}
                info={tripDetails || generalInfo}
                city={selectedDestination?.city}
                country={countryName}
                currency={currency}
              />
              {/* City Tabs */}
              <CityTabs
                destinations={(tripDetails || generalInfo)?.destinations}
                selectedCityIndex={selectedCityIndex}
                setSelectedCityIndex={setSelectedCityIndex}
                countryFlagLink={countryFlagLink}
              />

              {/* Itinerary */}
              <ItineraryTabs
                generalInfo={tripDetails || generalInfo}
                cityItinerary={cityItinerary}
                selectedDayIndex={selectedDayIndex}
                setSelectedDayIndex={setSelectedDayIndex}
                tripId={tripId}
                cityName={selectedDestination?.city}
                onActivitiesChange={handleActivitiesChange}
                cityTripPlan={cityTripPlan}
                itinerary={true}
                fromChatItineray={true}
                selectedCityIndex={selectedCityIndex}
                setSelectedCityIndex={setSelectedCityIndex}
              />

              <Box>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  mt={4}
                  mb={0}
                >
                  <HStack spacing={2}>
                    <Heading fontSize="18px" fontFamily="Arial" mb={0}>
                      <Translate text="Activities" />
                    </Heading>
                    {Array.isArray(selectedDestination?.viator_api_response) &&
                      selectedDestination?.viator_api_response.length > 0 && (
                        <FilterMenu
                          filterOptions={EXPLORE_SUBS}
                          selectedFilter={activityFilter}
                          onFilterChange={setActivityFilter}
                          multiple={true}
                        />
                      )}
                  </HStack>
                  {/* Delete all activities icon */}
                  {Array.isArray(selectedDestination?.viator_api_response) &&
                    selectedDestination?.viator_api_response.length > 0 && (
                      <Tooltip label="Delete all Activites">
                        <IconButton
                          aria-label="Delete all activities"
                          icon={<DeleteIcon />}
                          colorScheme="red"
                          size="sm"
                          isLoading={isDeletingAll}
                          onClick={() => openDeleteAllDialog("activities")}
                          ml={2}
                        />
                      </Tooltip>
                    )}
                </Flex>
                {Array.isArray(selectedDestination?.viator_api_response) &&
                selectedDestination?.viator_api_response.length > 0 ? (
                  <Activities
                    data={filteredActivities}
                    conversationId={tripId}
                    cityName={selectedDestination?.city}
                    onActivitiesChange={handleActivitiesChange}
                    fromChatItineray={true}
                  />
                ) : (
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    <Translate text="No activities created yet" />
                  </Text>
                )}
              </Box>

              <Box>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  mt={4}
                  mb={0}
                >
                  <HStack spacing={2}>
                    <Heading fontSize="18px" fontFamily="Arial" mb={0}>
                      <Translate text="Accommodations" />
                    </Heading>
                    {Array.isArray(
                      selectedDestination?.home_to_go_api_response
                    ) &&
                      selectedDestination?.home_to_go_api_response.length >
                        0 && (
                        <FilterMenu
                          filterOptions={ACCOMMODATION_SUBS}
                          selectedFilter={accommodationFilter}
                          onFilterChange={setAccommodationFilter}
                          multiple={true}
                        />
                      )}
                  </HStack>
                  {/* Delete all accommodations icon */}
                  {Array.isArray(
                    selectedDestination?.home_to_go_api_response
                  ) &&
                    selectedDestination?.home_to_go_api_response.length > 0 && (
                      <Tooltip label="Delete all Accommodations">
                        <IconButton
                          aria-label="Delete all accommodations"
                          icon={<DeleteIcon />}
                          colorScheme="red"
                          size="sm"
                          isLoading={isDeletingAllAccommodations}
                          onClick={() => openDeleteAllDialog("accommodations")}
                          ml={2}
                        />
                      </Tooltip>
                    )}
                </Flex>
                {Array.isArray(selectedDestination?.home_to_go_api_response) &&
                selectedDestination?.home_to_go_api_response.length > 0 ? (
                  <HotelsCard
                    hotels={filteredAccommodations}
                    conversationId={tripId}
                    cityName={selectedDestination?.city}
                    onAccommodationsChange={handleAccommodationsChange}
                    fromChatItineray={true}
                  />
                ) : (
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    <Translate text="No accommodations created yet" />
                  </Text>
                )}
              </Box>

              <Box>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  mt={4}
                  mb={0}
                >
                  <HStack spacing={2}>
                    <Heading fontSize="18px" fontFamily="Arial" mb={0}>
                      <Translate text="Transportation" />
                    </Heading>
                    {(Array.isArray(generalInfo?.flight_info) &&
                      generalInfo?.flight_info.length > 0) ||
                    (Array.isArray(
                      selectedDestination?.outdoorsy_api_response
                    ) &&
                      selectedDestination?.outdoorsy_api_response.length >
                        0) ? (
                      <FilterMenu
                        filterOptions={TRANSPORT_SUBS}
                        selectedFilter={transportationFilter}
                        onFilterChange={setTransportationFilter}
                        multiple={true}
                      />
                    ) : null}
                  </HStack>
                </Flex>

                {showFlights && (
                  <Box>
                    {generalInfo?.flight_info?.length > 0 && (
                      <Heading fontSize="16px" fontFamily="Arial" mt={4} mb={4}>
                        <Translate text="Flights" />
                      </Heading>
                    )}
                    {Array.isArray(generalInfo?.flight_info) &&
                    generalInfo?.flight_info?.length > 0
                      ? generalInfo?.flight_info?.map((item, idx) => (
                          <Box key={idx} mb={4}>
                            <Flex
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Heading
                                fontSize="16px"
                                fontFamily="Arial"
                                mt={2}
                                mb={0}
                              >
                                {formatRoute(item.route)}
                              </Heading>
                              <Tooltip label="Delete all Flights">
                                <IconButton
                                  aria-label="Delete all flights"
                                  icon={<DeleteIcon />}
                                  colorScheme="red"
                                  size="sm"
                                  isLoading={isDeletingAllFlights[item.route]}
                                  onClick={() =>
                                    openDeleteAllDialog("flights", item.route)
                                  }
                                  ml={2}
                                />
                              </Tooltip>
                            </Flex>
                            <FlightCard
                              data={filteredFlights[item.route] || item.flights}
                              conversationId={tripId}
                              route={item.route}
                              onFlightsChange={(flightIndex, type) =>
                                handleFlightsChange(
                                  item.route,
                                  flightIndex,
                                  type
                                )
                              }
                              fromChatItineray={true}
                            />
                          </Box>
                        ))
                      : null}
                  </Box>
                )}

                {showRVs && (
                  <Box>
                    {Array.isArray(
                      selectedDestination?.outdoorsy_api_response
                    ) &&
                    selectedDestination?.outdoorsy_api_response.length > 0 ? (
                      <>
                        <Flex
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Heading
                            fontSize="16px"
                            fontFamily="Arial"
                            mt={4}
                            mb={4}
                          >
                            <Translate text="RVs" />
                          </Heading>
                          <Tooltip label="Delete all RVs">
                            <IconButton
                              aria-label="Delete all RVs"
                              icon={<DeleteIcon />}
                              colorScheme="red"
                              size="sm"
                              isLoading={isDeletingAllRVs}
                              onClick={() => openDeleteAllDialog("rvs")}
                              ml={2}
                            />
                          </Tooltip>
                        </Flex>
                        <RVRentalCard
                          RVs={filteredRVs}
                          conversationId={tripId}
                          cityName={selectedDestination?.city}
                          onRVsChange={handleRVsChange}
                          fromChatItineray={true}
                        />
                      </>
                    ) : null}
                  </Box>
                )}
              </Box>

              {/* Show "No transportation created yet" when there are no flights and no RVs */}
              {(!Array.isArray(generalInfo?.flight_info) ||
                generalInfo?.flight_info.length === 0) &&
                (!Array.isArray(selectedDestination?.outdoorsy_api_response) ||
                  selectedDestination?.outdoorsy_api_response.length === 0) && (
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    <Translate text="No transportation created yet" />
                  </Text>
                )}

              {cityItinerary?.length > 0 && (
                <Box>
                  <Heading fontSize="18px" fontFamily="Arial" mt={4} mb={0}>
                    <Translate text="Top" />{" "}
                    {cityItinerary?.[0]?.location || "No Location"}{" "}
                    <Translate text="Travel Images" />
                  </Heading>

                  <Box
                    position="relative"
                    w="100%"
                    h={{ base: "32vh", md: "55vh", lg: "55vh" }}
                    overflow="hidden"
                  >
                    <HStack
                      spacing={4}
                      overflowX="auto"
                      maxW="100%"
                      ref={scrollContainerRef}
                      sx={{
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                        msOverflowStyle: "none",
                        scrollbarWidth: "none",
                      }}
                      p={2}
                    >
                      {cityItinerary.map((item, idx) => (
                        <Box
                          key={idx}
                          flexShrink={0}
                          width="calc(100% / 2.5)"
                          maxW="calc(100% / 2.5)"
                          height={{ base: "30vh", md: "53vh", lg: "53vh" }}
                        >
                          <Image
                            src={
                              item?.activity_detail_list?.[0]?.images?.[0]
                                ?.variants?.[2]?.url ||
                              "https://via.placeholder.com/150"
                            }
                            alt={item?.title || "No Title"}
                            objectFit="cover"
                            width="100%"
                            height="100%"
                            borderRadius="xl"
                          />
                        </Box>
                      ))}
                    </HStack>
                    <IconButton
                      aria-label="Scroll Left"
                      icon={<ChevronLeftIcon />}
                      onClick={scrollLeft}
                      isRound={true}
                      size="xs"
                      position="absolute"
                      top="50%"
                      left="10px"
                      transform="translateY(-50%)"
                      backgroundColor="white"
                      boxShadow="base"
                      zIndex="overlay"
                    />
                    <IconButton
                      aria-label="Scroll Right"
                      icon={<ChevronRightIcon />}
                      onClick={scrollRight}
                      isRound={true}
                      size="xs"
                      position="absolute"
                      top="50%"
                      right="10px"
                      transform="translateY(-50%)"
                      backgroundColor="white"
                      boxShadow="base"
                      zIndex="overlay"
                    />
                  </Box>
                </Box>
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </VStack>
      <AuthModal
        isOpen={isSignUpOpen}
        onClose={() => setSignUpOpen(false)}
        redirectPath="/ai-travel-planner/chat"
      />
      <SurveyPopup
        isOpen={isFeedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />
      {/* AlertDialog for Delete All actions */}
      <AlertDialog
        isOpen={!!pendingDelete.type}
        leastDestructiveRef={cancelRef}
        onClose={closeDeleteAllDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {pendingDelete.type === "activities" && "Delete All Activities"}
              {pendingDelete.type === "accommodations" &&
                "Delete All Accommodations"}
              {pendingDelete.type === "flights" && "Delete All Flights"}
              {pendingDelete.type === "rvs" && "Delete All RVs"}
            </AlertDialogHeader>
            <AlertDialogBody>
              {pendingDelete.type === "activities" &&
                `Are you sure you want to delete all activities for ${selectedDestination?.city}? This action cannot be undone.`}
              {pendingDelete.type === "accommodations" &&
                `Are you sure you want to delete all accommodations for ${selectedDestination?.city}? This action cannot be undone.`}
              {pendingDelete.type === "flights" &&
                `Are you sure you want to delete all flights for ${pendingDelete.route}? This action cannot be undone.`}
              {pendingDelete.type === "rvs" &&
                `Are you sure you want to delete all RVs for ${selectedDestination?.city}? This action cannot be undone.`}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeDeleteAllDialog}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  if (pendingDelete.type === "activities")
                    handleDeleteAllActivities();
                  else if (pendingDelete.type === "accommodations")
                    handleDeleteAllAccommodations();
                  else if (pendingDelete.type === "flights")
                    handleDeleteAllFlights(pendingDelete.route);
                  else if (pendingDelete.type === "rvs") handleDeleteAllRVs();
                }}
                ml={3}
                isLoading={
                  pendingDelete.type === "activities"
                    ? isDeletingAll
                    : pendingDelete.type === "accommodations"
                    ? isDeletingAllAccommodations
                    : pendingDelete.type === "flights"
                    ? isDeletingAllFlights[pendingDelete.route]
                    : pendingDelete.type === "rvs"
                    ? isDeletingAllRVs
                    : false
                }
              >
                Delete All
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ItineraryDetails;
